import { NgClass, NgIf, NgFor } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { Component, Input, Output, EventEmitter, HostListener, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, UserType } from '../../modules/auth/services/auth.service';
import { TranslationService, ILanguageTranslation } from "../../modules/i18n/translation.service"
import { LogoutService } from '../../shared/services/logout.service';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, switchMap, tap, catchError, finalize } from 'rxjs/operators';
import { UserInitialsUtil } from '../../shared/utils/user-initials.util';
import { PracticeService } from '../../services/accounts/practice.service';
import { PracticeChangeService } from '../../services/general/practice-change-service.service';
import { PracticeModel } from '../../models/accounts/practice.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertMessageService } from '../../modules/shared/services/alert-message.service';
import * as moment from 'moment';


import {NotificationService} from "../../services/accounts/notification.service"
import {NotificationModel, ResultNotificationModel} from "../../models/accounts/notification.model"
import {ThemeModeService, ThemeModeType} from "../../_metronic/partials/layout/theme-mode-switcher/theme-mode.service"


@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [FormsModule, NgClass, NgIf, NgFor, TooltipModule]
})
export class HeaderComponent implements OnInit, OnDestroy {
  /** El header acompaña el ancho del contenido (environment.fullWidthLayout). */
  @Input() fullWidth = false;

  @Output() toggleMenu = new EventEmitter<void>();

  // UI state
  isProfileDropdownOpen = false;
  isLanguageDropdownOpen = false;
  isPracticeDropdownOpen = false;

  // Notifications UI state
  isNotificationsOpen = false;
  loadingNotifications = false;

  // User / language
  selectedProvider: string = 'all';
  currentUser!: UserType;
  userInitials = 'JC';
  currentLanguage: ILanguageTranslation = { code: 'en', text: 'English' };
  availableLanguages: ILanguageTranslation[] = [];

  // Practices
  loading = false;
  practices: PracticeModel[] = [];
  filteredPractices: PracticeModel[] = [];
  selectedPractice: PracticeModel | null = null;

  // Search
  private readonly searchTerm$ = new Subject<string>();
  practiceSearchTerm = '';
  readonly limit = 100;
  readonly offset = 0;

  // i18n texts
  translations = {
    searchPlaceholder: 'Search practices...',
    loading: 'Searching server...',
    noResults: 'No practices found'
  };

  // lifecycle
  private readonly destroy$ = new Subject<void>();

  // storage keys
  private static readonly STORAGE_PRACTICE_ID = 'practiceId';
  private static readonly STORAGE_PRACTICE_NAME = 'practiceName';

  // Notifications data
  resultNotifications: ResultNotificationModel = new ResultNotificationModel();
  alerts: { icon: string; description: string; time: string; state: string | undefined; title: string }[] = [];
  unreadCount = 0;

  constructor(
    private readonly authService: AuthService,
    private readonly translationService: TranslationService,
    private readonly logoutService: LogoutService,
    private readonly router: Router,
    private readonly practiceService: PracticeService,
    private readonly practiceChangeService: PracticeChangeService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private alertMessage: AlertMessageService,
    private readonly notificationService: NotificationService,
    private readonly themeModeService: ThemeModeService
  ) {}

  // ---------- Theme mode ----------
  get isDarkMode(): boolean {
    return this.themeModeService.mode.value === 'dark';
  }

  toggleThemeMode(): void {
    const newMode: ThemeModeType = this.isDarkMode ? 'light' : 'dark';
    this.themeModeService.switchMode(newMode);
  }

  ngOnInit(): void {
    // User
    this.fetchNotifications();
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.userInitials = UserInitialsUtil.generateFromUser(this.currentUser, 'JC');
      });

    // Languages
    this.availableLanguages = this.translationService.getAvailableLanguages();
    this.updateCurrentLanguage();
    this.loadTranslations();

    // Practices initial load
    this.loadPractices();

    // Reactive search
    this.searchTerm$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        tap(() => this.setLoading(true)),
        switchMap(term =>
          this.practiceService.getPractices({ search: term }, this.limit, this.offset).pipe(
            catchError(err => {
              console.error('Error filtering practices:', err);
              this.filteredPractices = this.practices;
              return of({ results: this.filteredPractices });
            }),
            finalize(() => this.setLoading(false))
          )
        )
      )
      .subscribe((data: any) => {
        const results: PracticeModel[] = data?.results ?? [];
        if (this.practiceSearchTerm.trim()) {
          this.filteredPractices = results;
        } else {
          this.practices = results;
          this.filteredPractices = this.practices;
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- Language ----------
  private updateCurrentLanguage(): void {
    const selectedLang = this.translationService.getSelectedLanguage();
    this.currentLanguage =
      this.availableLanguages.find(lang => lang.code === selectedLang) ?? this.availableLanguages[0];
  }

  private loadTranslations(): void {
    this.translations = {
      searchPlaceholder: this.translateService.instant('PRACTICES.SEARCH'),
      loading: this.translateService.instant('PRACTICES.LOADING'),
      noResults: this.translateService.instant('PRACTICES.NO_RESULTS')
    };
  }

  getLanguageFlag(langCode: string): string {
    const flags: Record<string, string> = { en: '🇺🇸', es: '🇪🇸' };
    return flags[langCode] ?? '🌐';
  }

  toggleProfileDropdown(): void {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
    if (this.isProfileDropdownOpen) this.isLanguageDropdownOpen = false;
  }

  toggleLanguageDropdown(event: Event): void {
    event.stopPropagation();
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  selectLanguage(language: ILanguageTranslation, event: Event): void {
    event.stopPropagation();
    this.currentLanguage = language;
    this.translationService.setLanguage(language.code);
    this.closeAllDropdowns();
    this.loadTranslations();

    if (this.isPracticeDropdownOpen) this.filteredPractices = this.practices;
  }

  closeProfileDropdown(): void {
    this.isProfileDropdownOpen = false;
    this.isLanguageDropdownOpen = false;
  }

  // ---------- Practices ----------
  private setLoading(v: boolean): void {
    this.loading = v;
    this.cdr.detectChanges();
  }

  private loadPractices(): void {
    this.setLoading(true);
    this.practiceService
      .getPractices({ search: '' }, this.limit, this.offset)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          console.error('Error loading practices:', err);
          return of({ results: [] });
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe((data: any) => {
        this.practices = data?.results ?? [];
        this.filteredPractices = this.practices;
        this.loadSelectedPracticeFromStorage();
        this.cdr.markForCheck();
      });
  }

  loadSelectedPracticeFromStorage(): void {
    const practiceId = localStorage.getItem(HeaderComponent.STORAGE_PRACTICE_ID);
    this.selectedPractice =
      this.practices.find(p => p.id?.toString() === practiceId) ??
      this.practices[0] ??
      null;

    if (this.selectedPractice) {
      localStorage.setItem(HeaderComponent.STORAGE_PRACTICE_ID, this.selectedPractice.id?.toString() ?? '');
      localStorage.setItem(HeaderComponent.STORAGE_PRACTICE_NAME, this.selectedPractice.name ?? '');
    }
  }

  togglePracticeDropdown(): void {
    this.isPracticeDropdownOpen = !this.isPracticeDropdownOpen;

    if (this.isPracticeDropdownOpen) {
      // Cerrar otros
      this.isProfileDropdownOpen = false;
      this.isLanguageDropdownOpen = false;
      this.isNotificationsOpen = false;

      // Limpiar búsqueda y reusar la lista ya cargada; solo consulta el API si aún no hay datos
      this.practiceSearchTerm = '';
      if (this.practices.length) {
        this.filteredPractices = this.practices;
      } else {
        this.loadPractices();
      }

      // Focus en input de búsqueda
      setTimeout(() => {
        const searchInput = document.querySelector('.practice-search-input') as HTMLInputElement | null;
        if (searchInput) searchInput.focus();
        this.cdr.detectChanges();
      }, 100);
    }
  }

  onPracticeSearch(event: Event): void {
    const term = (event.target as HTMLInputElement)?.value?.trim() ?? '';
    this.practiceSearchTerm = term;
    this.searchTerm$.next(term);
  }

  selectPractice(practice: PracticeModel): void {
    this.selectedPractice = practice;

    localStorage.setItem(HeaderComponent.STORAGE_PRACTICE_ID, practice.id?.toString() ?? '');
    localStorage.setItem(HeaderComponent.STORAGE_PRACTICE_NAME, practice.name ?? '');
    this.alertMessage.showToast(
      `updated for Practice: ${ practice.name ?? '' }`,
      'success'
    );
    this.practiceChangeService.changePractice(practice);

    this.isPracticeDropdownOpen = false;
    this.practiceSearchTerm = '';

  }

  getPracticeDisplayText(practice: PracticeModel | null): string {
    if (!practice) return this.translateService.instant('PRACTICES.SELECT');
    return `${practice.nsi} - ${practice.name}`;
  }

  // ---------- Perfil ----------
  handleProfileAction(action: string): void {
    switch (action) {
      case 'language':
        break;
      case 'change-password':
        this.router.navigate(['/change-password']);
        break;
      case 'logout':
        this.logoutService
          .logout({ showConfirmation: true, redirectTo: '/auth/login', preserveLanguage: true })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: success => {},
            error: err => console.error('Logout error in header:', err)
          });
        break;
      default:
        break;
    }
    this.closeProfileDropdown();
  }

  private closeAllDropdowns(): void {
    this.isProfileDropdownOpen = false;
    this.isLanguageDropdownOpen = false;
    this.isPracticeDropdownOpen = false;
    this.isNotificationsOpen = false;
  }

  // ---------- Notificaciones ----------
  toggleNotifications(event: Event): void {
    event.stopPropagation();

    // Cerrar otros
    this.isProfileDropdownOpen = false;
    this.isLanguageDropdownOpen = false;
    this.isPracticeDropdownOpen = false;

    this.isNotificationsOpen = !this.isNotificationsOpen;

    if (this.isNotificationsOpen) {
      //this.fetchNotifications();
    }
  }

  private fetchNotifications(): void {
    this.loadingNotifications = true;


    this.notificationService
      .filter({'unread': 'true'}, 10, 0)
      .subscribe({
        next: (result: ResultNotificationModel) => {
          this.resultNotifications = result;

          // Usa count si lo expone tu API; si no, fallback al length de results
          this.unreadCount = (result as any).count ?? (result.results?.length ?? 0);

          this.alerts = (result.results || []).map((notification: NotificationModel) => {
            let icon: string;
            switch (notification.level) {
              case 'info':

                icon = 'icons/duotune/general/gen006.svg';
                break;
              case 'danger':
                icon = 'icons/duotune/general/gen044.svg';
                break;
              case 'warning':
                icon = 'icons/duotune/finance/fin006.svg';
                break;
              case 'success':
                icon = 'icons/duotune/files/fil023.svg';
                break;
              default:
                icon = 'icons/duotune/technology/teh008.svg';
                break;
            }

            return {
              title: notification.title,
              description: notification.message,
              time: moment(notification.created).fromNow(true),
              icon,
              state: notification.level
            };
          });

          this.loadingNotifications = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.resultNotifications = new ResultNotificationModel();
          this.alerts = [];
          this.unreadCount = 0;
          this.loadingNotifications = false;
          this.cdr.markForCheck();
        }
      });
  }

  goToNotifications(event?: Event): void {
    if (event) event.stopPropagation();
    this.isNotificationsOpen = false;
    this.router.navigate(['/notifications']);
  }
  goToInvitations(event?: Event): void {
    if (event) event.stopPropagation();
    this.isNotificationsOpen = false;
    this.router.navigate(['/invites/messages-invites']);
  }

  // ---------- Global listeners ----------
  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Profile
    const profileBtn = document.querySelector('.profile-btn');
    const profileDropdown = document.querySelector('.profile-dropdown');
    if (this.isProfileDropdownOpen && profileDropdown && profileBtn &&
      !profileDropdown.contains(target) && !profileBtn.contains(target)) {
      this.closeProfileDropdown();
    }

    // Language (submenu dentro del profile)
    const languageDropdown = document.querySelector('.language-dropdown');
    if (this.isLanguageDropdownOpen && languageDropdown &&
      !languageDropdown.contains(target) && !target.closest('.language-option')) {
      this.isLanguageDropdownOpen = false;
    }

    // Practice
    const practiceBtn = document.querySelector('.practice-dropdown-btn');
    const practiceDropdown = document.querySelector('.practice-dropdown');
    if (this.isPracticeDropdownOpen && practiceDropdown && practiceBtn &&
      !practiceDropdown.contains(target) && !practiceBtn.contains(target)) {
      this.isPracticeDropdownOpen = false;
    }

    // Notifications
    const notifBtn = document.querySelector('.notification-btn');
    const notifDropdown = document.querySelector('.notifications-dropdown');
    if (this.isNotificationsOpen && notifDropdown && notifBtn &&
      !notifDropdown.contains(target) && !notifBtn.contains(target)) {
      this.isNotificationsOpen = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(): void {
    this.closeAllDropdowns();
  }
}
